import React, { useState, useMemo } from "react";
import { format } from "date-fns";
import { Order } from "../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import CustomSelect from "../../../components/ui/CustomSelect";

interface OrdersTableProps {
  orders: Order[];
  onViewDetails: (order: Order) => void;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  onViewDetails,
  searchTerm = "",
  onSearchChange,
}) => {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Filter and search orders
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter((order) => order.order_type === filterType);
    }

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((order) => order.status === filterStatus);
    }

    // Search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((order) => {
        return (
          order.id.toLowerCase().includes(searchLower) ||
          (order.customer_name &&
            order.customer_name.toLowerCase().includes(searchLower)) ||
          (order.delivery_address &&
            order.delivery_address.toLowerCase().includes(searchLower)) ||
          (order.customer_phone && order.customer_phone.includes(searchTerm))
        );
      });
    }

    return filtered;
  }, [orders, filterType, filterStatus, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleOrders = filteredOrders.slice(startIndex, endIndex);

  // Calculate total income
  const totalIncome = useMemo(() => {
    return filteredOrders.reduce(
      (sum, order) => sum + (order.total_amount || 0),
      0
    );
  }, [filteredOrders]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDownload = () => {
    const csvRows = [
      [
        "Order ID",
        "Type",
        "Customer Name",
        "Customer Phone",
        "Delivery Address",
        "Status",
        "Total Amount",
        "Date",
      ],
      ...filteredOrders.map((order) => [
        order.id,
        order.order_type,
        order.customer_name || "",
        order.customer_phone || "",
        order.delivery_address || "",
        order.status,
        (order.total_amount || 0).toFixed(2),
        order.date ? format(new Date(order.date), "PPpp") : "",
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "orders.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowClick = (order: Order) => {
    onViewDetails(order);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSearchChange) {
      onSearchChange(event.target.value);
    }
    setCurrentPage(1); // Reset to first page on new search
  };

  return (
    <div className="w-full leading-normal bg-amber-50 bg-opacity-25 rounded-tl-xl rounded-tr-xl border rounded-xl overflow-x-hidden">
      <div className="flex flex-wrap justify-between items-center gap-3 p-5 bg-white rounded-lg shadow-sm min-w-0">
        {/* Search + Download */}
        <div className="flex flex-wrap items-center gap-3">
          {onSearchChange && (
            <input
              type="text"
              placeholder="Search by Order ID, Customer, Address, or Phone"
              value={searchTerm}
              onChange={handleSearch}
              className="border border-gray-300 focus:border-blue-400 focus:ring-blue-400 rounded-lg px-4 py-2 text-sm w-72 shadow-sm"
            />
          )}
          <button
            onClick={handleDownload}
            className="bg-yellow-400 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <FontAwesomeIcon icon={faDownload} />
            Download
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
            Filter by:
          </span>
          <div className="w-24 min-w-0">
            <CustomSelect
              value={filterType}
              onChange={(value) => {
                setFilterType(value as string);
                setCurrentPage(1);
              }}
              options={[
                { label: "All", value: "all" },
                { label: "Store", value: "store" },
                { label: "Parcel", value: "parcel" },
              ]}
              placeholder="Type"
            />
          </div>
          <div className="w-28 min-w-0">
            <CustomSelect
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value as string);
                setCurrentPage(1);
              }}
              options={[
                { label: "All", value: "all" },
                { label: "Pending", value: "pending" },
                { label: "Processing", value: "processing" },
                { label: "Delivered", value: "delivered" },
                { label: "Cancelled", value: "cancelled" },
              ]}
              placeholder="Status"
            />
          </div>
        </div>

        {/* Total Income */}
        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm">
          <span>Total Income:</span>
          <span>
            ₦
            {totalIncome.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 border-b border-amber-200 w-[60px]">
                S/N
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Order ID
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Type</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Customer Name
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Date</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Delivery Address
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Status</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Amount
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                View More
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.length > 0 ? (
              visibleOrders.map((order, index) => (
                <tr
                  key={order.id}
                  className="hover:bg-amber-50/80 cursor-pointer"
                  onClick={() => handleRowClick(order)}
                >
                  <td className="px-5 py-3 border-b border-amber-100">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {order.id}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.order_type === "parcel"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {order.order_type === "parcel" ? "Parcel" : "Store"}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className="block max-w-[200px] truncate cursor-pointer"
                      title={order.customer_name || "No customer name"}
                    >
                      {order.customer_name || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap">
                    {order.date ? format(new Date(order.date), "PP") : "—"}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className="block max-w-[200px] truncate cursor-pointer"
                      title={order.delivery_address || "No address"}
                    >
                      {order.delivery_address || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : order.status === "processing"
                          ? "bg-green-100 text-green-800"
                          : order.status === "delivered"
                          ? "bg-blue-100 text-blue-800"
                          : order.status === "cancelled"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap">
                    ₦
                    {(order.total_amount || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <img src={removeRedEye} alt="View" className="w-6 h-6" />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="px-5 py-4 text-center text-sm text-gray-500"
                >
                  No orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center p-4">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="self-center">
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersTable;
